import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {Puzzle} from './puzzle.js';
import {GameFlowController} from './game-flow-controller.js';
import {TutorialSpawner} from './tutorial-spawner.js';
import {PopupWindowController, PopupWindowPanelStyle} from '../ui/panels/popup-window.js';
import {Localization} from '../utils/Localization.js';
import {waitForCondition, waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {DropMode} from './puzzle-camera.js';
import {TutorialGameRules} from './tutorial-game-rules.js';
import {UIModel} from '../models/UIModel.js';
import {PowerupManager} from './powerup-manager.js';
import {BombButtonTutorial} from './bomb-button-tutorial.js';
import {TextManager} from '../classes/text-manager.js';
import {TutorialFingerTypes} from '../enums/TutorialFingerTypes.js';

export class TutorialTextScript extends Component {
    static TypeName = 'tutorial-text-script';

    private _coroutineManager = new CoroutineManager();

    //TK: The original game started the tutorial spawning, but since in our game it
    // is already there we need to start it manually.
    startTutorial() {
        this._runTutorialId = this._coroutineManager.addCoroutine(
            new Coroutine(this._runTutorial())
        );
    }

    update(delta: number): void {
        this._coroutineManager.update(delta);
    }

    private _runTutorialId = 0;
    private *_runTutorial() {
        const puzzle = Puzzle.instance;
        const hudModel = puzzle.score.HUDModel;
        const gameFlowController = GameFlowController.instance;
        const spawner = puzzle.spawner as TutorialSpawner;

        const popup = PopupWindowController.instance;
        const puzzleCam = puzzle.mainCamera;
        puzzleCam.dropImmediateAllowed = false;
        puzzleCam.allowRotation = false;
        let closestColumn = 0;
        UIModel.instance.setTutorialHUDActive(true);

        gameFlowController.pause();

        // Make sure the texts are loaded before starting the tutorial
        yield* waitForCondition(() => TextManager.instance.isLoaded);

        yield* waitForCondition(() => gameFlowController.canChangeMenu);
        // Let's go!
        hudModel.setMalSpeech(TextManager.instance.getInGameTutorialText(0));
        popup.setPanel(PopupWindowPanelStyle.ingame);
        // popup.show(Localization.instance.get('TutorialHowToPlay1'), true);
        // yield* waitForCondition(() => !popup.isVisible);
        // yield* waitForCondition(() => gameFlowController.canChangeMenu);

        // popup.show(Localization.instance.get('TutorialHowToPlay2'), true);
        // gameFlowController.pause();

        // yield* waitForCondition(() => !popup.isVisible);
        gameFlowController.unpause();
        spawner.spawn();
        spawner.paused = true;
        yield* waitForSeconds(1.2);

        gameFlowController.pause();

        // 	Animation wordTween = wordBal.GetComponent<Animation>();
        // 	wordTween.Play("WordBalloonIn");
        yield* waitForCondition(() => gameFlowController.canChangeMenu);
        // How to rotate
        popup.show(Localization.instance.get('TutorialHowToRotate'), false);

        // spawn how to rotate finger
        hudModel.setTutorialFinger(TutorialFingerTypes.Rotation);
        puzzleCam.allowRotation = true;
        let lastLane = puzzleCam.findClosestLane();
        let laneCount = 0;
        // 	QualityControlManager.GetInstance().StartSampling();
        do {
            let currentLane = puzzleCam.findClosestLane();
            if (currentLane != lastLane) {
                laneCount++;
                lastLane = currentLane;
            }
            yield null;
        } while (laneCount < 8);
        // 	QualityControlManager.GetInstance().StopSampling();
        popup.hide();
        hudModel.setTutorialFinger(TutorialFingerTypes.None);
        gameFlowController.unpause();
        // Rotation skill learned!
        // how many blobs on the board?
        let startingNumberOfBlobs = puzzle.objectCount();
        // "look out below!"
        // 	wordTween.Rewind();
        // 	wordTween.Play("WordBalloonIn");
        hudModel.setMalSpeech(TextManager.instance.getInGameTutorialText(1));

        let loadedObjectCount = puzzle.loadedObjectCount();
        // 	QualityControlManager.GetInstance().StartSampling();
        // Wait for first blob embed...
        yield* waitForCondition(() => puzzle.loadedObjectCount() != loadedObjectCount);
        // gameFlowController.pause();
        spawner.paused = true;
        // 	QualityControlManager.GetInstance().StopSampling();
        yield* waitForCondition(() => gameFlowController.canChangeMenu);

        // Fruit Blob Color Match, blob pop
        popup.show(Localization.instance.get('TutorialHowToPop'), false);
        // spawn how to pop finger
        hudModel.setTutorialFinger(TutorialFingerTypes.PopBlob);

        closestColumn = puzzle.findFirstLoadedObject().column;
        puzzleCam.angle = puzzleCam.angleForLane(closestColumn);

        puzzleCam.allowRotation = false;
        // 	QualityControlManager.GetInstance().StartSampling();
        yield* waitForCondition(() => puzzle.objectCount() < startingNumberOfBlobs);

        // 	QualityControlManager.GetInstance().StopSampling();
        puzzleCam.allowRotation = true;
        // "chain reaction!"
        // 	wordTween.Rewind();
        // 	wordTween.Play("WordBalloonIn");
        hudModel.setMalSpeech(TextManager.instance.getInGameTutorialText(2));
        // turn complete
        BombButtonTutorial.instance.completeTurn();
        hudModel.setTutorialFinger(TutorialFingerTypes.None);
        popup.hide();
        loadedObjectCount = puzzle.loadedObjectCount();
        // 	// Popping skill learned!
        spawner.paused = false;

        yield* waitForSeconds(1.2);
        spawner.paused = true;
        yield* waitForCondition(() => gameFlowController.canChangeMenu);

        // Fast Drop
        popup.show(Localization.instance.get('TutorialFastDrop'), false);
        puzzleCam.dropImmediateAllowed = true;
        puzzleCam.allowRotation = false;
        gameFlowController.pause();

        // spawn how to drag fast finger
        hudModel.setTutorialFinger(TutorialFingerTypes.Swipe);
        yield* waitForCondition(() => puzzleCam.currentDropMode === DropMode.Immediate);

        hudModel.setTutorialFinger(TutorialFingerTypes.None);
        popup.hide();
        gameFlowController.unpause();
        puzzleCam.allowRotation = false;

        // turn complete
        BombButtonTutorial.instance.completeTurn();

        // Fast drop learned!
        startingNumberOfBlobs = puzzle.objectCount();

        // "pop that blob!"
        // 	wordTween.Rewind();
        // 	wordTween.Play("WordBalloonIn");
        hudModel.setMalSpeech(TextManager.instance.getInGameTutorialText(3));
        // 	QualityControlManager.GetInstance().StartSampling();

        // Wait for next chain
        while (puzzle.objectCount() >= startingNumberOfBlobs) {
            BombButtonTutorial.instance.enabled = false;
            yield null;
        }
        // 	QualityControlManager.GetInstance().StopSampling();
        BombButtonTutorial.instance.enabled = true;
        spawner.paused = true;
        yield waitForSeconds(1.5);

        hudModel.setTutorialFinger(TutorialFingerTypes.Powerup);
        yield* waitForCondition(() => gameFlowController.canChangeMenu);

        popup.show(Localization.instance.get('TutorialBombs'), false);
        // "try out that powerup"
        // 	wordTween.Rewind();
        // 	wordTween.Play("WordBalloonIn");
        hudModel.setMalSpeech(TextManager.instance.getInGameTutorialText(4));
        yield* waitForCondition(() => spawner.queueTop() != null);

        hudModel.setTutorialFinger(TutorialFingerTypes.None);
        popup.hide();
        let loadedObjectCount2 = puzzle.loadedObjectCount();
        spawner.paused = false;
        // 	QualityControlManager.GetInstance().StartSampling();
        let beforeBombBlobs = puzzle.objectCount();
        // 	// Wait for first blob embed...
        yield* waitForCondition(() => puzzle.loadedObjectCount() != loadedObjectCount2);

        // "BOOOOOOM"
        // 	wordTween.Rewind();
        // 	wordTween.Play("WordBalloonIn");
        hudModel.setMalSpeech(TextManager.instance.getInGameTutorialText(5));
        yield* waitForCondition(() => puzzle.objectCount() < beforeBombBlobs);

        yield* waitForSeconds(0.5);
        // 	QualityControlManager.GetInstance().StopSampling();

        // tell more about bombs
        popup.show(Localization.instance.get('TutorialBombsExplanation'), true);
        yield* waitForCondition(() => !popup.isVisible);

        const tutRules = puzzle.rules as TutorialGameRules;
        tutRules.setTutorialDone();

        //show winner text
        puzzle.score.showMaxedLevelText();
        hudModel.setMalSpeech('');

        // reset values
        puzzleCam.dropImmediateAllowed = true;
        puzzleCam.allowRotation = true;

        // 	Destroy(bombButtonRef);
    }
}
